import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { dashboardApi, type DashboardRangeParams } from '@/api';
import { queryKeys } from '@/lib/queryClient';
import { useResolvedRange } from '@/store/dateFilterStore';
import type { DateRange } from '@/types';

/**
 * Dashboard queries.
 *
 * Two things every hook here shares:
 *
 * - **The topbar window is read internally.** No screen has to thread `from`/`to`
 *   through its props, and no widget can accidentally be left un-filtered.
 * - **`enabled` is a real parameter.** The dashboard hands each section its stage
 *   gate, which is what makes the page fill in top-to-bottom instead of firing
 *   seven requests at once. A disabled query costs nothing.
 *
 * `keepPreviousData` matters more than it looks: changing the date filter then
 * keeps the previous numbers on screen, dimmed, instead of collapsing every card
 * back to a skeleton — the difference between a dashboard that feels instant and
 * one that appears to reload.
 */

interface Options {
  enabled?: boolean;
  /**
   * Opt out of the topbar filter.
   *
   * The scanner screens use this: they have no picker of their own, and a
   * receiver's "Received today" tile must never quietly show last month's total
   * because someone set a filter on the desktop dashboard earlier.
   */
  ignoreDateFilter?: boolean;
}

/** The `from`/`to` pair, or `{}` when the filter is off. */
function useWindow(ignore = false): DashboardRangeParams {
  const range = useResolvedRange();
  return range.active && !ignore ? { from: range.from, to: range.to } : {};
}

export const useDashboardSummary = ({ enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.summary(dateWindow),
    queryFn: () => dashboardApi.summary(dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useDailyActivity = (range: DateRange, { enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.dailyActivity(range, dateWindow),
    queryFn: () => dashboardApi.dailyActivity(range, dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useQrStatusOverview = ({ enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.qrStatus(dateWindow),
    queryFn: () => dashboardApi.qrStatus(dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useBuyerWiseQr = (period: DateRange, limit = 5, { enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.buyerWise(period, { ...dateWindow, limit }),
    queryFn: () => dashboardApi.buyerWise(period, limit, dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useRecentActivities = (limit = 5, { enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.recentActivities(limit, dateWindow),
    queryFn: () => dashboardApi.recentActivities(limit, dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const usePoSummary = (limit = 5, { enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.poSummary(limit, dateWindow),
    queryFn: () => dashboardApi.poSummary(limit, dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useTodaySummary = ({ enabled = true, ignoreDateFilter }: Options = {}) => {
  const dateWindow = useWindow(ignoreDateFilter);
  return useQuery({
    queryKey: queryKeys.dashboard.todaySummary(dateWindow),
    queryFn: () => dashboardApi.todaySummary(dateWindow),
    placeholderData: keepPreviousData,
    enabled,
  });
};
